// src/components/Login.tsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import css from './Login.module.css';
import { LoginType, useAuthContext } from '../../auth/AuthStateProvider';

const Login: React.FC = () => {
  const { login } = useAuthContext();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const handleGoogleCredentialResponse = async (response: any) => {
      try {
        await login(LoginType.google, response.credential);
        navigate('/');
      } catch {
        setErr('Google sign-in failed.');
      }
    };

    const googleExists = () => {
      // @ts-ignore
      return window.google && window.google.accounts;
    };

    const initializeGoogleSignIn = () => {
      if (!googleExists()) return;
      // @ts-ignore
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCredentialResponse,
      });
      // @ts-ignore
      window.google.accounts.id.renderButton(
        document.getElementById('google-signin'),
        { theme: 'outline', size: 'standard' }
      );
    };

    let interval: NodeJS.Timeout | undefined;
    if (googleExists()) {
      initializeGoogleSignIn();
    } else {
      interval = setInterval(() => {
        if (googleExists()) {
          initializeGoogleSignIn();
          if (interval) clearInterval(interval);
        }
      }, 100);
    }

    return () => { if (interval) clearInterval(interval); };
  }, [login, navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await login(LoginType.password, email, password);
      navigate('/');
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Invalid credentials.');
    } finally {
      setBusy(false);
    }
  }

  const features = [
    { eyebrow: 'Recipes', title: 'A working library', body: 'Specs, build steps, taste maps, garnish discipline. Everything the team needs to pour the same drink twice.' },
    { eyebrow: 'Cellar', title: 'Bottle by bottle', body: 'Stock takes, par lines, reorder triggers. The cellar laid out the way it actually sits — by shelf and color.' },
    { eyebrow: 'Menus', title: 'Compose & share', body: 'Pull from cocktails, spirits, or wine. Print the menu, share a link. The list updates as the program does.' },
    { eyebrow: 'Notes', title: 'Bar-team memory', body: 'Comments per recipe. Substitutions, prep timing, allocation notes — held next to the spec, not lost in chat.' },
  ];

  return (
    <div className={css.loginContainer} style={{ minHeight: '100vh', padding: '40px 24px' }}>
      <header style={{ textAlign: 'center', marginBottom: 36 }}>
        <span className="brand" style={{ fontSize: 24 }}>Bar&nbsp;Almanac</span>
      </header>

      <section style={{ maxWidth: 460, margin: '0 auto' }}>
        <div className="card" style={{ padding: 28 }}>
          <div style={{ textAlign: 'center', marginBottom: 18 }}>
            <span className="eyebrow eyebrow--plum">— Sign in</span>
            <h1 className="display" style={{ fontSize: 32, marginTop: 6, letterSpacing: '-0.02em' }}>
              Pour with intent.
            </h1>
            <p className="lede" style={{ marginTop: 6 }}>A working bar program for a small, serious team.</p>
          </div>

          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 36, marginBottom: 12 }}>
              <div id="google-signin" />
            </div>
          )}

          <div className="rule"><span className="rule__label">or</span></div>

          <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
            <div className="field">
              <label>Email</label>
              <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label>Password</label>
              <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {err && <div style={{ color: '#B43838', fontSize: 12.5 }}>{err}</div>}
            <button type="submit" disabled={busy} className="btn btn--plum" style={{ justifyContent: 'center', height: 40 }}>
              {busy ? '…' : 'Sign in'}
            </button>
          </form>

          <p className="muted" style={{ textAlign: 'center', marginTop: 14, fontSize: 12.5 }}>
            New here? <Link to="/signup" className="link">Create an account</Link>
          </p>
        </div>
      </section>

      <section style={{ maxWidth: 880, margin: '48px auto 0' }}>
        <div className="rule"><span className="rule__label">What you get</span></div>
        <div className="grid grid--2" style={{ marginTop: 14 }}>
          {features.map((f, i) => (
            <article key={i} className="card">
              <div className="card__head">
                <div className="card__sub">{f.eyebrow}</div>
                <div className="card__title" style={{ fontSize: 19 }}>{f.title}</div>
              </div>
              <div className="card__notes">{f.body}</div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Login;
